import { Box, Flex, Text, Badge, chakra } from '@chakra-ui/react';
import { X, Calendar, AlignLeft, Users, Building2, type LucideIcon } from 'lucide-react';
import { useModalScrollLock } from '@/hooks/useModalScrollLock';
import { ExpandableClampText } from '@/pages/Volunteers/components/ExpandableClampText';
import type { Task } from './volunteerTaskUtils';
import { formatDeadlineWithWeekday } from './volunteerTaskUtils';

interface TaskInfoModalProps {
  task: Task | null;
  open: boolean;
  onClose: () => void;
  currentUserId: string;
  profileNameById: Map<string, string>;
}

export function TaskInfoModal({
  task,
  open,
  onClose,
  currentUserId,
  profileNameById,
}: TaskInfoModalProps) {
  useModalScrollLock(open);

  if (!open || !task) return null;

  const volunteers = task.assigned_volunteers ?? [];
  const otherIds = volunteers.filter((id) => id && id !== currentUserId);
  const otherNames = otherIds.map((id) => profileNameById.get(id) ?? 'Volunteer');
  const departments = (task.assigned_departments ?? []).map((d) => d?.trim()).filter(Boolean);
  const descriptionText = task.description?.trim() ?? '';

  return (
    <Box
      position="fixed"
      inset="0"
      bg="rgba(15, 23, 42, 0.45)"
      backdropFilter="blur(4px)"
      display="flex"
      alignItems="center"
      justifyContent="center"
      zIndex={1100}
      px="4"
      py="6"
      onClick={onClose}
    >
      <Box
        bg="white"
        borderRadius="3xl"
        w="full"
        maxW="440px"
        maxH="88vh"
        overflow="hidden"
        display="flex"
        flexDirection="column"
        border="1px solid"
        borderColor="volyx.mediumGray"
        boxShadow="0 25px 50px -12px rgba(31, 41, 50, 0.25), 0 0 0 1px rgba(255,255,255,0.06) inset"
        onClick={(e: React.MouseEvent) => e.stopPropagation()}
      >
        {/* Header */}
        <Flex
          align="flex-start"
          justify="space-between"
          gap="3"
          px="6"
          pt="6"
          pb="4"
          bg="linear-gradient(135deg, #1F2932 0%, #2d3a47 100%)"
        >
          <Box minW="0" flex="1">
            <Text
              fontSize="xs"
              fontWeight="semibold"
              letterSpacing="0.12em"
              textTransform="uppercase"
              m="0"
              mb="2"
              color="rgba(255,255,255,0.92)"
            >
              Task details
            </Text>
            <Text
              fontSize="lg"
              fontWeight="bold"
              lineHeight="short"
              m="0"
              color="#FFFFFF"
              textShadow="0 1px 2px rgba(0,0,0,0.35)"
              css={{
                display: '-webkit-box',
                WebkitLineClamp: 4,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden',
                wordBreak: 'break-word',
              }}
            >
              {task.title}
            </Text>
          </Box>
          <chakra.button
            type="button"
            bg="rgba(255,255,255,0.15)"
            border="none"
            cursor="pointer"
            w="36px"
            h="36px"
            borderRadius="full"
            display="flex"
            alignItems="center"
            justifyContent="center"
            flexShrink={0}
            transition="background 0.15s ease"
            _hover={{ bg: 'rgba(255,255,255,0.25)' }}
            onClick={onClose}
            aria-label="Close"
          >
            <X size={18} color="white" />
          </chakra.button>
        </Flex>

        {/* Body */}
        <Box
          px="5"
          py="5"
          overflowY="auto"
          flex="1"
          bg="linear-gradient(180deg, #fafbfc 0%, #ffffff 120px)"
        >
          <Flex direction="column" gap="3">
            <DetailBlock
              icon={AlignLeft}
              label="Description"
              iconBg="rgba(0, 122, 255, 0.1)"
              iconColor="#007AFF"
            >
              {descriptionText ? (
                <ExpandableClampText
                  text={descriptionText}
                  lines={2}
                  fontSize="sm"
                  color="volyx.text"
                  moreLabel="See more"
                  lessLabel="See less"
                />
              ) : (
                <Text fontSize="sm" color="volyx.textSecondary" m="0" lineHeight="tall">
                  No description added for this task.
                </Text>
              )}
            </DetailBlock>

            <DetailBlock
              icon={Calendar}
              label="Deadline"
              iconBg="rgba(52, 199, 89, 0.12)"
              iconColor="#34C759"
            >
              <Flex align="center" gap="2" flexWrap="wrap">
                <Text fontSize="sm" fontWeight="semibold" color="volyx.text" m="0">
                  {task.deadline ? formatDeadlineWithWeekday(task.deadline) : 'No deadline set'}
                </Text>
              </Flex>
            </DetailBlock>

            <DetailBlock
              icon={Users}
              label="Other volunteers"
              iconBg="rgba(255, 149, 0, 0.12)"
              iconColor="#FF9500"
            >
              {otherNames.length > 0 ? (
                <Flex gap="2" flexWrap="wrap">
                  {otherNames.map((name, i) => (
                    <Badge
                      key={`${otherIds[i]}-${i}`}
                      px="3"
                      py="1"
                      borderRadius="full"
                      fontSize="sm"
                      fontWeight="medium"
                      colorPalette="gray"
                      variant="surface"
                    >
                      {name}
                    </Badge>
                  ))}
                </Flex>
              ) : (
                <Text fontSize="sm" color="volyx.textSecondary" m="0" lineHeight="tall">
                  No other volunteers assigned individually.
                </Text>
              )}
            </DetailBlock>

            <DetailBlock
              icon={Building2}
              label="Department assignment"
              iconBg="rgba(31, 41, 50, 0.08)"
              iconColor="#1F2932"
            >
              {departments.length > 0 ? (
                <Flex gap="2" flexWrap="wrap">
                  {departments.map((d) => (
                    <Badge
                      key={d}
                      px="3"
                      py="1"
                      borderRadius="lg"
                      fontSize="sm"
                      fontWeight="medium"
                      borderWidth="1px"
                      borderColor="volyx.mediumGray"
                      bg="white"
                      color="volyx.text"
                    >
                      {d}
                    </Badge>
                  ))}
                </Flex>
              ) : (
                <Text fontSize="sm" color="volyx.textSecondary" m="0">
                  None
                </Text>
              )}
            </DetailBlock>
          </Flex>
        </Box>
      </Box>
    </Box>
  );
}

function DetailBlock({
  icon: Icon,
  label,
  children,
  iconBg,
  iconColor,
}: {
  icon: LucideIcon;
  label: string;
  children: React.ReactNode;
  iconBg: string;
  iconColor: string;
}) {
  return (
    <Box
      bg="white"
      borderRadius="2xl"
      border="1px solid"
      borderColor="rgba(31, 41, 50, 0.08)"
      p="4"
      boxShadow="0 1px 3px rgba(31, 41, 50, 0.04)"
    >
      <Flex align="center" gap="2.5" mb="2.5">
        <Flex
          w="36px"
          h="36px"
          borderRadius="xl"
          align="center"
          justify="center"
          flexShrink={0}
          bg={iconBg}
        >
          <Icon size={18} color={iconColor} aria-hidden />
        </Flex>
        <Text fontSize="xs" fontWeight="bold" color="volyx.textSecondary" m="0" letterSpacing="wide" textTransform="uppercase">
          {label}
        </Text>
      </Flex>
      <Box>{children}</Box>
    </Box>
  );
}
